'use client'

import { usePathname } from 'next/navigation'
import type { FC } from 'react'
import { useEffect } from 'react'
import React, { createContext, useCallback, useContext, useState } from 'react'
import type { LastBrowsedLocation } from '../../types'
import {
  getLocationFromUrl,
  propagateLastBrowsedLocation,
  updateLastBrowsedLocation,
} from '../../use-cases'
import type { CitiesSlugs } from '@/city/types'
import type { DeliveryAddress } from '@/delivery-address/entities'
import { consoleLog } from '@/tools/console-log'
import { glovoRum, useErrorHandling } from '@/tools/o11y/client'

type LastBrowserLocationProviderProps = {
  children: React.ReactNode
  initialLocation: LastBrowsedLocation
  citiesSlugs: CitiesSlugs
}

type LastBrowsedLocationContextType = {
  lastBrowsedLocation: LastBrowsedLocation
  setLastBrowsedLocation: (options: Partial<LastBrowsedLocation>, trigger?: string) => void
  setLocationFromDeliveryAddress: (deliveryAddress: DeliveryAddress | null) => Promise<void>
}

const LastBrowsedLocationContext = createContext<LastBrowsedLocationContextType>({
  setLastBrowsedLocation: (_options) => undefined,
  setLocationFromDeliveryAddress: async (_deliveryAddress) => undefined,
  lastBrowsedLocation: {
    cityCode: null,
    countryCode: null,
    locationCoordinates: null,
  },
})

export const LastBrowserLocationProvider: FC<LastBrowserLocationProviderProps> = ({
  children,
  initialLocation,
  citiesSlugs,
}) => {
  const [isInitialised, setIsInitialised] = useState(false)
  const pathName = usePathname()
  const { execWithErrorTracking } = useErrorHandling()
  const [deliveryAddress, setDeliveryAddress] = useState<DeliveryAddress | null>(null)

  const [lastBrowsedLocationInternal, setLastBrowsedLocationInternal] =
    useState<LastBrowsedLocation>(initialLocation)

  const setLastBrowsedLocation = useCallback(
    (newLocation: Partial<LastBrowsedLocation>, trigger?: string) => {
      if (!newLocation.locationCoordinates) {
        const cityMatchesAddress =
          deliveryAddress?.cityCode && newLocation.cityCode === deliveryAddress.cityCode
        if (cityMatchesAddress) {
          newLocation.locationCoordinates = {
            latitude: deliveryAddress.latitude,
            longitude: deliveryAddress.longitude,
          }
        }
      }
      const updatedLocation = updateLastBrowsedLocation(lastBrowsedLocationInternal, newLocation)
      setLastBrowsedLocationInternal(updatedLocation)
      propagateLastBrowsedLocation(newLocation)
      consoleLog.cyan('LastBrowsedLocation', `[${trigger}]] New location set:`, updatedLocation)
      glovoRum.addAction('location.last-browsed-location.new-location-set', {
        location: updatedLocation,
        trigger,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveryAddress]
  )

  const setLocationFromDeliveryAddress = useCallback(
    async (address: DeliveryAddress | null) => {
      if (address) {
        setDeliveryAddress(address)
        setLastBrowsedLocation(
          {
            cityCode: address.cityCode,
            countryCode: address.countryCode,
            locationCoordinates: {
              latitude: address.latitude,
              longitude: address.longitude,
            },
          },
          'deliveryAddress'
        )
      }
    },
    [setLastBrowsedLocation]
  )

  useEffect(() => {
    execWithErrorTracking(() => {
      consoleLog.cyan('LastBrowsedLocation', 'Initializing', initialLocation)
      propagateLastBrowsedLocation(initialLocation)
      setIsInitialised(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation])

  useEffect(() => {
    execWithErrorTracking(() => {
      const location = getLocationFromUrl(pathName, citiesSlugs)
      if (isInitialised && location) {
        setLastBrowsedLocation(location, 'pathName')
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName, setLastBrowsedLocation])

  return (
    <LastBrowsedLocationContext.Provider
      value={{
        lastBrowsedLocation: lastBrowsedLocationInternal,
        setLastBrowsedLocation,
        setLocationFromDeliveryAddress,
      }}
    >
      {children}
    </LastBrowsedLocationContext.Provider>
  )
}

export const useLastBrowsedLocation = () => useContext(LastBrowsedLocationContext)
